import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { Markdown } from "tiptap-markdown";
import type { Transaction } from "@tiptap/pm/state";
import { useCallback, useEffect, useRef, useState } from "react";
import { imageFilesFrom, uploadImage } from "@/lib/uploadImage";

export interface MarkdownEditorProps {
  /** Form field name, e.g. "post[body]". The value is written to a hidden
   *  input under this name, so the server contract is unchanged: it still
   *  receives Markdown from an ordinary form post. */
  name: string;
  value: string;
  placeholder?: string;
  /** Endpoint that accepts an image and answers { url, alt }. From a Rails
   *  helper, never built here. */
  uploadUrl: string;
}

interface ToolbarButtonProps {
  label: string;
  title: string;
  isActive: boolean;
  onClick: () => void;
}

function ToolbarButton({
  label,
  title,
  isActive,
  onClick,
}: ToolbarButtonProps) {
  return (
    <button
      type="button"
      title={title}
      aria-pressed={isActive}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={`rounded px-2 py-1 text-sm ${
        isActive
          ? "bg-slate-900 text-white"
          : "text-slate-600 hover:bg-slate-100"
      }`}
    >
      {label}
    </button>
  );
}

interface ToolbarProps {
  editor: Editor;
  onFilesChosen: (files: File[]) => void;
  isUploading: boolean;
  uploadError: string | null;
}

function Toolbar({
  editor,
  onFilesChosen,
  isUploading,
  uploadError,
}: ToolbarProps) {
  const fileInput = useRef<HTMLInputElement>(null);

  // Commands run through here rather than being called directly so a command
  // that does not move the caret still re-renders the toolbar; caret movement
  // itself is covered by onSelectionUpdate on the editor.
  const [, setTick] = useState(0);
  const run = useCallback(
    (command: () => void) => () => {
      command();
      setTick((value) => value + 1);
    },
    [],
  );

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 px-2 py-1">
      <ToolbarButton
        label="B"
        title="Bold"
        isActive={editor.isActive("bold")}
        onClick={run(() => editor.chain().focus().toggleBold().run())}
      />
      <ToolbarButton
        label="I"
        title="Italic"
        isActive={editor.isActive("italic")}
        onClick={run(() => editor.chain().focus().toggleItalic().run())}
      />
      <span className="mx-1 h-4 w-px bg-slate-300" />
      <ToolbarButton
        label="H2"
        title="Heading"
        isActive={editor.isActive("heading", { level: 2 })}
        onClick={run(() =>
          editor.chain().focus().toggleHeading({ level: 2 }).run(),
        )}
      />
      <ToolbarButton
        label="H3"
        title="Subheading"
        isActive={editor.isActive("heading", { level: 3 })}
        onClick={run(() =>
          editor.chain().focus().toggleHeading({ level: 3 }).run(),
        )}
      />
      <span className="mx-1 h-4 w-px bg-slate-300" />
      <ToolbarButton
        label="• List"
        title="Bullet list"
        isActive={editor.isActive("bulletList")}
        onClick={run(() => editor.chain().focus().toggleBulletList().run())}
      />
      <ToolbarButton
        label="1. List"
        title="Numbered list"
        isActive={editor.isActive("orderedList")}
        onClick={run(() => editor.chain().focus().toggleOrderedList().run())}
      />
      <span className="mx-1 h-4 w-px bg-slate-300" />
      <ToolbarButton
        label="Quote"
        title="Blockquote"
        isActive={editor.isActive("blockquote")}
        onClick={run(() => editor.chain().focus().toggleBlockquote().run())}
      />
      <ToolbarButton
        label="Code"
        title="Code block"
        isActive={editor.isActive("codeBlock")}
        onClick={run(() => editor.chain().focus().toggleCodeBlock().run())}
      />
      <span className="mx-1 h-4 w-px bg-slate-300" />
      <ToolbarButton
        label={isUploading ? "Uploading\u2026" : "Image"}
        title="Insert an image"
        isActive={false}
        onClick={() => fileInput.current?.click()}
      />
      {/* Outside the form's own fields: this input is a file picker for the
          toolbar, not something the post submits. */}
      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/avif"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared so choosing the same file twice in a row still fires.
          event.target.value = "";
          if (file) onFilesChosen([file]);
        }}
      />
      {uploadError ? (
        <span role="alert" className="ml-2 text-sm text-red-700">
          {uploadError}
        </span>
      ) : null}
    </div>
  );
}

export function MarkdownEditor({
  name,
  value,
  placeholder,
  uploadUrl,
}: MarkdownEditorProps) {
  // The hidden input, not the editor, is what the form submits. Seeding it with
  // the incoming value means a post saved without touching the body still
  // round-trips its own Markdown rather than submitting empty.
  const [markdown, setMarkdown] = useState(value);

  // The server renders more Markdown than this editor's schema can hold, and
  // the mismatch is silent and destructive: an image round-trips to an empty
  // string, a table to its cell text run together. Anyone opening such a post
  // and saving would destroy it without a warning. So the editor proves it can
  // represent this body before offering to edit it, and steps aside when it
  // cannot. That covers every construct the schema lacks, including ones added
  // to the renderer later, which chasing extensions one at a time would not.
  const [canRepresentBody, setCanRepresentBody] = useState(true);

  const [uploadError, setUploadError] = useState<string | null>(null);
  // Counted, not a flag: a paste can start while a drop is still uploading, and
  // whichever finished first used to clear the spinner for both.
  const [uploadsInFlight, setUploadsInFlight] = useState(0);
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  // dragenter and dragleave fire for every child the pointer crosses, so the
  // highlight flickers if it tracks them directly. Depth counts how many
  // nested elements the drag is currently inside.
  const dragDepth = useRef(0);

  // Where the next image goes. A drop position is a document offset, and the
  // writer keeps typing while the upload runs -- so it is mapped forward
  // through every transaction below rather than used raw seconds later.
  const insertAt = useRef<number | null>(null);

  // handleDrop and handlePaste are built into the editor's options, which are
  // read before `editor` exists, so they cannot close over it. The ref is
  // pointed at the real implementation once there is an editor to insert into.
  const insertImagesRef = useRef<(files: File[], at?: number) => void>(
    () => {},
  );

  const editor = useEditor({
    // inline, which is not the extension's default. As a block node the
    // serializer drops the blank line after an image, so a body with one
    // between two paragraphs comes back with the next paragraph glued onto it
    // -- corruption at rest, on a body the editor claimed it could represent.
    extensions: [StarterKit, Image.configure({ inline: true }), Markdown],
    content: value,
    editorProps: {
      attributes: {
        class: "prose prose-slate max-w-none p-3 outline-none min-h-[24rem]",
        "aria-label": placeholder ?? "Post body",
      },
      handleDrop(view, event, _slice, moved) {
        // `moved` is the editor rearranging its own content; only files
        // arriving from outside are ours.
        if (moved) return false;

        const files = imageFilesFrom((event as DragEvent).dataTransfer?.files);
        if (files.length === 0) return false;

        event.preventDefault();
        dragDepth.current = 0;
        setIsDraggingImage(false);

        // Where the pointer let go, not where the caret happens to be.
        const at = view.posAtCoords({
          left: (event as DragEvent).clientX,
          top: (event as DragEvent).clientY,
        })?.pos;
        insertImagesRef.current(files, at);

        return true;
      },
      handlePaste(_view, event) {
        const clipboard = (event as ClipboardEvent).clipboardData;
        const files = imageFilesFrom(clipboard?.files);
        if (files.length === 0) return false;

        // Excel and Word put a bitmap of the selection on the clipboard beside
        // its markup. Both describe one copy, and the markup is the one the
        // writer meant -- taking the image would replace their table with a
        // picture of it, with nothing to undo back to.
        if (clipboard?.types.includes("text/html")) return false;

        // Left to the browser, a pasted image becomes a data: URI, which the
        // renderer empties -- so the picture would vanish on save rather than
        // fail visibly. Uploading it is the fix, not the convenience.
        event.preventDefault();
        insertImagesRef.current(files);

        return true;
      },
    },
    onCreate: ({ editor }) => {
      setCanRepresentBody(
        editor.storage.markdown.getMarkdown().trim() === value.trim(),
      );
    },
    onUpdate: ({ editor }) =>
      setMarkdown(editor.storage.markdown.getMarkdown()),
    // Active states are read from the editor, which mutates in place without
    // re-rendering React. Without this, moving the caret into bold text leaves
    // the B button reading unpressed.
    onSelectionUpdate: () => setSelectionTick((tick) => tick + 1),
  });

  const [, setSelectionTick] = useState(0);

  useEffect(() => {
    if (!editor) return;

    // Mapping covers our own insertions too, so a second image lands after the
    // first rather than on top of it.
    const remapInsertPoint = ({
      transaction,
    }: {
      transaction: Transaction;
    }) => {
      if (insertAt.current === null || !transaction.docChanged) return;
      insertAt.current = transaction.mapping.map(insertAt.current);
    };

    editor.on("transaction", remapInsertPoint);
    return () => {
      editor.off("transaction", remapInsertPoint);
    };
  }, [editor]);

  const insertImages = useCallback(
    async (files: File[], at?: number) => {
      if (!editor) return;

      setUploadError(null);
      setUploadsInFlight((count) => count + 1);
      insertAt.current = at ?? editor.state.selection.from;

      // Sequential rather than parallel: the order the writer dropped them in
      // is the order they should appear.
      const failures: string[] = [];

      for (const file of files) {
        try {
          const image = await uploadImage(file, uploadUrl);

          // insertContentAt rather than focus().setImage(): focusing would
          // pull the caret out of whatever the writer moved on to while the
          // upload ran, and land the rest of their sentence in the body.
          editor.commands.insertContentAt(insertAt.current ?? 0, {
            type: "image",
            attrs: { src: image.url, alt: image.alt },
          });
        } catch (error) {
          console.error("Image upload failed", error);
          failures.push(
            `${file.name}: ${
              error instanceof Error ? error.message : "could not be uploaded"
            }`,
          );
        }
      }

      insertAt.current = null;
      // Named individually, because one failure used to abandon every file
      // after it with nothing on screen to say they had ever been dropped.
      if (failures.length > 0) setUploadError(failures.join(" "));
      setUploadsInFlight((count) => count - 1);
    },
    [editor, uploadUrl],
  );

  insertImagesRef.current = (files, at) => void insertImages(files, at);

  if (!canRepresentBody) {
    return (
      <div>
        <textarea
          name={name}
          defaultValue={value}
          rows={18}
          aria-label={placeholder ?? "Post body"}
          className="w-full rounded border border-slate-300 px-3 py-2 font-mono text-sm"
        />
        <p className="mt-1 text-sm text-amber-700">
          This post uses Markdown the rich editor cannot represent — an image, a
          table or a task list. Editing it as Markdown keeps it intact.
        </p>
      </div>
    );
  }

  // dataTransfer.files is empty until the drop, but items exposes each entry's
  // kind and type throughout the drag -- only the data itself is withheld. So
  // the highlight can promise exactly what the drop will accept, rather than
  // lighting up for a PDF it is going to refuse.
  const carriesImage = (event: React.DragEvent) =>
    Array.from(event.dataTransfer.items).some(
      (item) => item.kind === "file" && item.type.startsWith("image/"),
    );

  return (
    <div>
      {/* The drop itself is handled by the editor, which knows where in the
          document the pointer is. These only light the target up, so a writer
          dragging a file can see it will land. */}
      <div
        onDragEnter={(event) => {
          if (!carriesImage(event)) return;
          dragDepth.current += 1;
          setIsDraggingImage(true);
        }}
        onDragOver={(event) => {
          // Cancelling dragover is what makes this a valid drop target. It has
          // to cover every file drag, not just the images: a drag left
          // uncancelled here is one the drop handler below never gets to stop.
          if (Array.from(event.dataTransfer.types).includes("Files")) {
            event.preventDefault();
          }
        }}
        onDragLeave={() => {
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setIsDraggingImage(false);
        }}
        onDrop={(event) => {
          // Cancelling dragover above only made this a target -- it does not
          // suppress the drop's default action, which is to navigate to the
          // file and take the unsaved post with it. ProseMirror's handleDrop
          // cancels the drops it takes, but it never sees a drop on the
          // toolbar, and it declines anything that is not an image. Both land
          // inside the box the highlight has just lit up.
          event.preventDefault();
          dragDepth.current = 0;
          setIsDraggingImage(false);
        }}
        className={`overflow-hidden rounded border transition-colors ${
          isDraggingImage
            ? "border-slate-900 bg-slate-50"
            : "border-slate-300 focus-within:border-slate-500"
        }`}
      >
        {editor ? (
          <Toolbar
            editor={editor}
            onFilesChosen={(files) => void insertImages(files)}
            isUploading={uploadsInFlight > 0}
            uploadError={uploadError}
          />
        ) : null}
        <EditorContent editor={editor} />
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Drag an image in, paste one, or use the Image button.
      </p>
      <input type="hidden" name={name} value={markdown} />
    </div>
  );
}
